import React from 'react';
import { Form, Input, Row, Col, Select, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { customizeRequiredMark, fieldDefaultProps } from 'utils/fieldConfig';
import dayjs from 'dayjs';
import { formatCardNumber, validateCardNumber, getCardType } from 'utils/cardFormatting';

interface PaymentMethodFormProps {
  form: any;
  onFinish: (values: any) => void;
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({ form, onFinish }) => {
  const { t } = useTranslation();

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cardType = form.getFieldValue('cardType') || getCardType(value);
    const formattedValue = formatCardNumber(value, cardType);
    form.setFieldValue('cardNumber', formattedValue);
  };

  const handleCardTypeChange = (value: string) => {
    const cardNumber = form.getFieldValue('cardNumber');
    if (cardNumber) {
      const formattedValue = formatCardNumber(cardNumber, value);
      form.setFieldValue('cardNumber', formattedValue);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={customizeRequiredMark}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Form.Item
            label={t('profile.paymentMethods.form.fields.cardHolderName')}
            name="cardHolderName"
            rules={[{ required: true, message: t('profile.paymentMethods.form.rules.requiredCardHolderName') }]}
          >
            <Input {...fieldDefaultProps} placeholder={t('profile.paymentMethods.form.placeholder.cardHolderName')} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label={t('profile.paymentMethods.form.fields.cardNumber')}
            name="cardNumber"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: t('profile.paymentMethods.form.rules.requiredCardNumber') },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const cardType = form.getFieldValue('cardType') || getCardType(value);
                  if (!validateCardNumber(value, cardType)) {
                    return Promise.reject(t('profile.paymentMethods.form.rules.invalidCardNumber'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              {...fieldDefaultProps}
              placeholder={t('profile.paymentMethods.form.placeholder.cardNumber')}
              onChange={handleCardNumberChange}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t('profile.paymentMethods.form.fields.expiryDate')}
            name="expiryDate"
            rules={[{ required: true, message: t('profile.paymentMethods.form.rules.requiredExpiryDate') }]}
          >
            <DatePicker
              {...fieldDefaultProps}
              placeholder={t('profile.paymentMethods.form.placeholder.expiryDate')}
              format="MM/YY"
              picker="month"
              disabledDate={(current) => current && current < dayjs().startOf('month')}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t('profile.paymentMethods.form.fields.cardCvv')}
            name="cardCvv"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: t('profile.paymentMethods.form.rules.requiredCardCvv') },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!/^\d{3,4}$/.test(value)) {
                    return Promise.reject(t('profile.paymentMethods.form.rules.invalidCardCvv'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              {...fieldDefaultProps}
              placeholder={t('profile.paymentMethods.form.placeholder.cardCvv')}
              maxLength={4}
              type="password"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label={t('profile.paymentMethods.form.fields.cardType')}
            name="cardType"
            rules={[{ required: true, message: t('profile.paymentMethods.form.rules.requiredCardType') }]}
          >
            <Select
              {...fieldDefaultProps}
              placeholder={t('profile.paymentMethods.form.placeholder.cardType')}
              onChange={handleCardTypeChange}
            >
              <Select.Option value="visa">Visa</Select.Option>
              <Select.Option value="mastercard">Mastercard</Select.Option>
              <Select.Option value="amex">American Express</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default PaymentMethodForm;
